import React, { useState, useEffect, useMemo } from 'react';

/**
 * HfpV2Report
 * -----------
 * Reusable renderer for the Holistic Functional Profile (v2) report in the
 * live portal. It takes the output of the backend `build_full_report`
 * (`build_full_report` keys such as `about_this_profile`, `at_a_glance_table`,
 * `strengths`, `challenges_summary`, `areas_of_development`,
 * `suggested_adjustments`, `gpi_areas`, `sensory_profile`,
 * `environmental_summary`, plus optional LLM keys `_polished`,
 * `_strengths_llm`, `_challenges_llm`, `_gpi_polished`, `_big_picture_llm`,
 * `_llm_meta`) and renders the sidebar-nav + sectioned layout.
 *
 * The rendering logic here is intentionally self-contained — it duplicates
 * the inline `ReportDisplay` of the HFP v2 demo page so the demo can remain
 * untouched. Every section is defensive: missing/empty data renders an empty
 * or placeholder state rather than throwing. An error boundary wraps the
 * content pane so a malformed section cannot blank the page.
 *
 * Props:
 *   report:    plain object from `build_full_report`.
 *   childName: optional display name; falls back to `report.child_name`
 *              then to "the child".
 *   devMode:   optional; when true exposes evidence/debug panels (defaults
 *              to false so the live portal shows the clean end-user view).
 */

// ────────────────────────────────────────────────────────────────────────────
// CLASSIFICATION → VISUAL BUCKET MAPPING
// ────────────────────────────────────────────────────────────────────────────

const CLASSIFICATION_TO_BUCKET = {
  'Strength': 'STRENGTH_NO_DIFFICULTY',
  'No difficulty': 'STRENGTH_NO_DIFFICULTY',
  'Mild difficulty': 'SUPPORT_NEED',
  'Moderate or severe difficulty': 'PRIORITY_AREA',
  'A strength': 'STRENGTH_NO_DIFFICULTY',
  'No concerns': 'STRENGTH_NO_DIFFICULTY',
  'Some difficulty': 'SUPPORT_NEED',
  'Quite difficult': 'PRIORITY_AREA',
  'Very difficult': 'PRIORITY_AREA',
  'Not enough information': 'UNKNOWN',
  // HFP v3 family word set (TD-210): the adapter passes these labels verbatim;
  // Strength folds into the green family word, and environmental areas read
  // the barrier/facilitator parallel set so a family never sees "Priority
  // area" for their child's environment.
  'Strength / no difficulty': 'STRENGTH_NO_DIFFICULTY',
  'Support need': 'SUPPORT_NEED',
  'Priority area': 'PRIORITY_AREA',
  'Insufficient data': 'UNKNOWN',
  'Not applicable': 'UNKNOWN',
  'Facilitators in place': 'STRENGTH_NO_DIFFICULTY',
  'No barriers': 'STRENGTH_NO_DIFFICULTY',
  'Barriers present': 'SUPPORT_NEED',
  'Severe barriers': 'PRIORITY_AREA',
};

const BUCKET_STYLES = {
  STRENGTH_NO_DIFFICULTY: {
    badge: 'bg-[#e1f0c8] text-green-800 border-green-200',
    row: 'bg-[#e1f0c8]',
    label: 'Strength / No difficulty',
  },
  SUPPORT_NEED: {
    badge: 'bg-[#fef3cc] text-yellow-900 border-yellow-200',
    row: 'bg-[#fef3cc]',
    label: 'Support need',
  },
  PRIORITY_AREA: {
    badge: 'bg-[#ffdfde] text-red-800 border-red-200',
    row: 'bg-[#ffdfde]',
    label: 'Priority area',
  },
  UNKNOWN: {
    badge: 'bg-gray-100 text-gray-700 border-gray-200',
    row: 'bg-gray-50',
    label: '—',
  },
};

function getBucket(classification) {
  return CLASSIFICATION_TO_BUCKET[classification] || 'UNKNOWN';
}

function getBucketStyle(classification) {
  return BUCKET_STYLES[getBucket(classification)] || BUCKET_STYLES.UNKNOWN;
}

// ────────────────────────────────────────────────────────────────────────────
// SMALL PRESENTATIONAL HELPERS
// ────────────────────────────────────────────────────────────────────────────

/** Section header card — left-aligned by default for readability */
function SectionHeaderCard({ title, subtitle }) {
  return (
    <div className="rounded-2xl bg-white px-5 py-5 md:px-8">
      <div className="text-xl md:text-2xl font-extrabold text-[#4a2c5a]">{title}</div>
      {subtitle && <div className="mt-1.5 text-sm md:text-base text-[#4a2c5a]">{subtitle}</div>}
    </div>
  );
}

/** Blue-tinted narrative card */
function SummaryBox({ text }) {
  if (!text) return null;
  return (
    <div className="mt-5 rounded-2xl bg-blue-50 border border-blue-200 border-l-4 border-l-blue-300 p-5 md:p-6">
      <p className="text-sm leading-relaxed text-[#4a2c5a] whitespace-pre-wrap">{text}</p>
    </div>
  );
}

/**
 * Classification badge pill. When `mean` is provided, an on-hover tooltip
 * shows the numeric score and threshold ranges.
 */
function ClassificationBadge({ classification, mean }) {
  const style = getBucketStyle(classification);
  const label = classification || '—';

  const tooltipLines = [];
  if (mean != null) {
    tooltipLines.push(`Mean: ${mean.toFixed(2)}  →  ${label}`);
    tooltipLines.push('Thresholds: >0 Strength | 0 No concerns | ≥-1 Some difficulty | ≥-2.5 Quite difficult | <-2.5 Very difficult');
  }
  const tooltipText = tooltipLines.join('\n') || undefined;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${style.badge} ${mean != null ? 'cursor-help' : ''}`}
      title={tooltipText}
    >
      {label}
    </span>
  );
}

/** Colour key legend */
function ColourKeyLegend() {
  const items = [
    { label: 'Strength / No difficulty', swatch: 'bg-green-600' },
    { label: 'Support need', swatch: 'bg-yellow-400' },
    { label: 'Priority area', swatch: 'bg-red-400' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600 mb-4">
      {items.map((it) => (
        <div key={it.label} className="flex items-center gap-1.5">
          <span className={`w-3 h-3 rounded-full ${it.swatch}`} />
          <span>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// AREAS OF DEVELOPMENT — DOMAIN METADATA
// ────────────────────────────────────────────────────────────────────────────
// Each domain has a nav label, subtitle for the page header, and a matching
// key into the backend's areas_of_development dict. Order defines sidebar order.

const AOD_DOMAINS = [
  {
    id: 'aod-ef',
    label: 'Executive Functions',
    domainKey: 'Executive Function',
    subtitle: (name) => `Mental skills that help ${name} plan, focus, and achieve his goals`,
  },
  {
    id: 'aod-sensory',
    label: 'Sensory',
    domainKey: 'Sensory',
    subtitle: (name) => `How ${name} experiences and processes sensory information`,
  },
  {
    id: 'aod-social',
    label: 'Social & Emotional',
    domainKey: 'Social & Emotional',
    subtitle: (name) => `How ${name} understands and manages feelings and relationships`,
  },
  {
    id: 'aod-motor',
    label: 'Movement & Coordination',
    domainKey: 'Motor & Physical',
    subtitle: (name) => `Physical skills that help ${name} move, write, and explore the world`,
  },
  {
    id: 'aod-slc',
    label: 'Speech, Language & Communication',
    domainKey: 'Speech, Language & Communication',
    subtitle: (name) => `How ${name} understands, uses, and expresses language`,
  },
];

// Maps a classification label to the bucket-aware visual config used in the
// "AT HOME / AT SCHOOL" status panels.
const STATUS_PANEL_CONFIG = {
  STRENGTH_NO_DIFFICULTY: {
    bg: 'bg-[#e8f5e0]',
    text: 'Strength / No difficulty',
    pillColour: 'bg-green-500',
    pillBorder: 'border-[#4a2c5a]',
    pills: [true, true, true],
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-green-700" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
      </svg>
    ),
  },
  SUPPORT_NEED: {
    bg: 'bg-[#fef3cc]',
    text: 'Support need',
    pillColour: 'bg-yellow-400',
    pillBorder: 'border-[#4a2c5a]',
    pills: [true, true, false],
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-yellow-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 11L12 6L14 8" /><path d="M17 13L12 18L10 16" />
        <path d="M7 11C4.5 13 4.5 17 7 19C9.5 21 13.5 21 16 19" />
        <path d="M17 13C19.5 11 19.5 7 17 5C14.5 3 10.5 3 8 5" />
      </svg>
    ),
  },
  // A place marker, not a warning sign (TDI-505): a priority area is where
  // support matters most, and a parent reading alone must not read it as an
  // alarm. The word and the row colour carry the level together with the mark,
  // so the meaning never rides on colour alone. The same marker is used for
  // sensation avoidance below, which sat on the same triangle.
  PRIORITY_AREA: {
    bg: 'bg-[#ffe5e5]',
    text: 'Priority area',
    pillColour: 'bg-red-500',
    pillBorder: 'border-[#4a2c5a]',
    pills: [true, false, false],
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#43213e]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  UNKNOWN: {
    bg: 'bg-gray-100',
    text: '—',
    pillColour: 'bg-gray-300',
    pillBorder: 'border-[#4a2c5a]',
    pills: [false, false, false],
    icon: null,
  },
};

// Sensory-specific panel configs keyed by the label returned from the backend.
// Avoidance = red, all other tags = yellow, no difficulty = green,
// insufficient data = grey.
const SENSORY_PANEL_CONFIG = {
  'Sensation Avoidance': {
    bg: 'bg-[#ffe5e5]', text: 'Sensation Avoidance',
    pillColour: 'bg-red-500', pillBorder: 'border-[#4a2c5a]',
    pills: [true, false, false],
    textColour: 'text-red-800',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#43213e]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  'High Sensitivity': {
    bg: 'bg-[#fef3cc]', text: 'High Sensitivity',
    pillColour: 'bg-yellow-400', pillBorder: 'border-[#4a2c5a]',
    pills: [true, true, false],
    textColour: 'text-yellow-800',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-yellow-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 11L12 6L14 8" /><path d="M17 13L12 18L10 16" />
        <path d="M7 11C4.5 13 4.5 17 7 19C9.5 21 13.5 21 16 19" />
        <path d="M17 13C19.5 11 19.5 7 17 5C14.5 3 10.5 3 8 5" />
      </svg>
    ),
  },
  'Low Sensitivity': {
    bg: 'bg-[#fef3cc]', text: 'Low Sensitivity',
    pillColour: 'bg-yellow-400', pillBorder: 'border-[#4a2c5a]',
    pills: [true, true, false],
    textColour: 'text-yellow-800',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-yellow-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 11L12 6L14 8" /><path d="M17 13L12 18L10 16" />
        <path d="M7 11C4.5 13 4.5 17 7 19C9.5 21 13.5 21 16 19" />
        <path d="M17 13C19.5 11 19.5 7 17 5C14.5 3 10.5 3 8 5" />
      </svg>
    ),
  },
  'Sensation Seeking': {
    bg: 'bg-[#fef3cc]', text: 'Sensation Seeking',
    pillColour: 'bg-yellow-400', pillBorder: 'border-[#4a2c5a]',
    pills: [true, true, false],
    textColour: 'text-yellow-800',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-yellow-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 11L12 6L14 8" /><path d="M17 13L12 18L10 16" />
        <path d="M7 11C4.5 13 4.5 17 7 19C9.5 21 13.5 21 16 19" />
        <path d="M17 13C19.5 11 19.5 7 17 5C14.5 3 10.5 3 8 5" />
      </svg>
    ),
  },
  'Low Registration': {
    bg: 'bg-[#fef3cc]', text: 'Low Registration',
    pillColour: 'bg-yellow-400', pillBorder: 'border-[#4a2c5a]',
    pills: [true, true, false],
    textColour: 'text-yellow-800',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-yellow-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 11L12 6L14 8" /><path d="M17 13L12 18L10 16" />
        <path d="M7 11C4.5 13 4.5 17 7 19C9.5 21 13.5 21 16 19" />
        <path d="M17 13C19.5 11 19.5 7 17 5C14.5 3 10.5 3 8 5" />
      </svg>
    ),
  },
  'High Registration': {
    bg: 'bg-[#fef3cc]', text: 'High Registration',
    pillColour: 'bg-yellow-400', pillBorder: 'border-[#4a2c5a]',
    pills: [true, true, false],
    textColour: 'text-yellow-800',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-yellow-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 11L12 6L14 8" /><path d="M17 13L12 18L10 16" />
        <path d="M7 11C4.5 13 4.5 17 7 19C9.5 21 13.5 21 16 19" />
        <path d="M17 13C19.5 11 19.5 7 17 5C14.5 3 10.5 3 8 5" />
      </svg>
    ),
  },
  'No Difficulty': {
    bg: 'bg-[#e8f5e0]', text: 'No Difficulty',
    pillColour: 'bg-green-500', pillBorder: 'border-[#4a2c5a]',
    pills: [true, true, true],
    textColour: 'text-green-800',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-green-700" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
      </svg>
    ),
  },
  'Not enough information': {
    bg: 'bg-gray-100', text: 'Not enough information',
    pillColour: 'bg-gray-300', pillBorder: 'border-[#4a2c5a]',
    pills: [false, false, false],
    textColour: 'text-gray-400',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  },
};

function getSensoryConfig(label) {
  if (!label) return { config: SENSORY_PANEL_CONFIG['Not enough information'], displayLabel: '—' };
  if (SENSORY_PANEL_CONFIG[label]) return { config: SENSORY_PANEL_CONFIG[label], displayLabel: label };

  // Compound labels (e.g. "High Sensitivity & Low Registration"):
  // avoidance takes priority (red), otherwise first matched tag (yellow).
  if (label.includes('Avoidance')) return { config: SENSORY_PANEL_CONFIG['Sensation Avoidance'], displayLabel: label };
  for (const key of Object.keys(SENSORY_PANEL_CONFIG)) {
    if (label.includes(key)) return { config: SENSORY_PANEL_CONFIG[key], displayLabel: label };
  }
  return { config: SENSORY_PANEL_CONFIG['Not enough information'], displayLabel: label };
}

/**
 * Status panel showing "AT HOME" or "AT SCHOOL". Both sensory and non-sensory
 * panels share the same layout: icon, context label, classification text, and
 * 3-segment pill bar.
 */
function StatusPanel({ context, classification, mean, isSensory, devMode, labelText }) {
  const contextLabel = context === 'home' ? 'AT HOME' : 'AT SCHOOL';

  if (isSensory) {
    const { config: sc, displayLabel } = getSensoryConfig(classification);
    const isDisabled = classification === 'Not enough information' || !classification;

    return (
      <div className={`w-[34rem] rounded-xl ${sc.bg} px-4 py-3 ${isDisabled ? 'opacity-60' : ''}`}>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 self-center">{sc.icon}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1.5 whitespace-nowrap">
              <span className={`font-bold text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>{contextLabel}</span>
              <span className={`text-sm italic ${sc.textColour}`}>– {displayLabel}</span>
              {devMode && mean != null && (
                <span className="text-[10px] text-gray-400 tabular-nums font-mono">
                  ({mean.toFixed(2)})
                </span>
              )}
            </div>
            <div className="flex gap-1.5 mt-2">
              {sc.pills.map((lit, i) => (
                <div
                  key={i}
                  className={`h-3.5 rounded-sm border-2 ${
                    lit
                      ? `${sc.pillColour} ${sc.pillBorder}`
                      : `bg-white ${sc.pillBorder}`
                  }`}
                  style={{ width: '25%' }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const bucket = getBucket(classification);
  const config = STATUS_PANEL_CONFIG[bucket] || STATUS_PANEL_CONFIG.UNKNOWN;

  const tooltipLines = [];
  if (devMode && mean != null) {
    tooltipLines.push(`Mean score: ${mean.toFixed(2)}`);
    tooltipLines.push(`Classification: ${classification || '—'}`);
    tooltipLines.push('');
    tooltipLines.push('Thresholds:');
    tooltipLines.push('  > 0     → A strength');
    tooltipLines.push('    0     → No concerns');
    tooltipLines.push(' ≥ -1    → Some difficulty');
    tooltipLines.push(' ≥ -2.5  → Quite difficult');
    tooltipLines.push('  < -2.5 → Very difficult');
  }
  const tooltipText = tooltipLines.join('\n');

  return (
    <div
      className={`w-96 rounded-xl ${config.bg} px-4 py-3 ${mean != null ? 'cursor-help' : ''}`}
      title={tooltipText || undefined}
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 self-center">{config.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5">
            <span className="font-bold text-sm text-gray-900">{contextLabel}</span>
            <span className="text-sm italic text-gray-700">– {labelText || config.text}</span>
            {devMode && mean != null && (
              <span className="text-[10px] text-gray-400 tabular-nums font-mono">
                ({mean.toFixed(2)})
              </span>
            )}
          </div>
          <div className="flex gap-1.5 mt-2">
            {config.pills.map((lit, i) => (
              <div
                key={i}
                className={`h-3.5 rounded-sm border-2 ${
                  lit
                    ? `${config.pillColour} ${config.pillBorder}`
                    : 'bg-white border-[#4a2c5a]'
                }`}
                style={{ width: '25%' }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Sidebar colour key legend displayed at the bottom of the sidebar. */
function SidebarColourKey() {
  const items = [
    { ...STATUS_PANEL_CONFIG.STRENGTH_NO_DIFFICULTY, label: 'Strength / No difficulty' },
    { ...STATUS_PANEL_CONFIG.SUPPORT_NEED, label: 'Support need' },
    { ...STATUS_PANEL_CONFIG.PRIORITY_AREA, label: 'Priority Area' },
  ];
  return (
    <div className="border-t border-gray-200 bg-white/80 p-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Key</div>
      <div className="space-y-2.5">
        {items.map((item) => (
          <div key={item.label} className={`rounded-lg px-3 py-2 ${item.bg}`}>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                {item.icon}
              </div>
              <div className="flex-1 text-left">
                <span className="text-[10px] font-semibold text-gray-800 whitespace-nowrap">{item.label}</span>
                <div className="flex gap-1 mt-1">
                  {item.pills.map((lit, i) => (
                    <div
                      key={i}
                      className={`h-2.5 rounded-sm border-2 ${
                        lit
                          ? `${item.pillColour} ${item.pillBorder}`
                          : 'bg-white border-[#4a2c5a]'
                      }`}
                      style={{ width: '24%' }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Collapsible debug panel for the report engine's decision trace. Only renders
 * when dev mode is on and notes are present; hidden for end users.
 */
function DebugPanel({ notes, hidden }) {
  const [open, setOpen] = useState(false);
  if (hidden || !notes || notes.length === 0) return null;
  return (
    <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50/60">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100/50 transition-colors rounded-lg"
      >
        <span>🔍 Debug: Decision Trace ({notes.length} note{notes.length !== 1 ? 's' : ''})</span>
        <span className="text-amber-600">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-1">
          {notes.map((note, i) => (
            <div
              key={i}
              className="text-xs text-amber-900 font-mono leading-relaxed pl-3 border-l-2 border-amber-300"
            >
              {note}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible panel displaying the raw questionnaire answers backing a report
 * paragraph. Only renders in dev mode with evidence present.
 */
function EvidencePanel({ items, hidden }) {
  const [open, setOpen] = useState(false);
  if (hidden || !items || items.length === 0) return null;
  return (
    <div className="mt-3 rounded-lg border border-sky-300 bg-sky-50/60">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-sky-800 hover:bg-sky-100/50 transition-colors rounded-lg"
      >
        <span>📋 Source Evidence ({items.length} item{items.length !== 1 ? 's' : ''})</span>
        <span className="text-sky-600">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="px-4 pb-3 overflow-x-auto">
          <table className="w-full text-xs mt-1">
            <thead>
              <tr className="border-b border-sky-200 text-sky-900">
                <th className="py-1.5 pr-3 text-left font-semibold">Item</th>
                <th className="py-1.5 pr-3 text-left font-semibold">Description</th>
                <th className="py-1.5 pr-3 text-left font-semibold">Subdomain</th>
                <th className="py-1.5 pr-2 text-left font-semibold">Home Answer</th>
                <th className="py-1.5 pr-2 text-left font-semibold">Score</th>
                <th className="py-1.5 pr-2 text-left font-semibold">School Answer</th>
                <th className="py-1.5 text-left font-semibold">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sky-100">
              {items.map((ev) => (
                <tr key={ev.item_code} className="text-sky-900/80">
                  <td className="py-1.5 pr-3 font-mono whitespace-nowrap">{ev.item_code}</td>
                  <td className="py-1.5 pr-3">{ev.description}</td>
                  <td className="py-1.5 pr-3 text-sky-700">{ev.subdomain}</td>
                  <td className="py-1.5 pr-2">{ev.home_answer}</td>
                  <td className="py-1.5 pr-2 text-left tabular-nums font-medium">
                    {ev.home_score != null ? ev.home_score.toFixed?.(1) ?? ev.home_score : '—'}
                  </td>
                  <td className="py-1.5 pr-2">{ev.school_answer}</td>
                  <td className="py-1.5 text-left tabular-nums font-medium">
                    {ev.school_score != null ? ev.school_score.toFixed?.(1) ?? ev.school_score : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// EVIDENCE POPUP (magnifying glass overlay) — dev mode only
// ────────────────────────────────────────────────────────────────────────────

function EvidencePopup({ popup, onClose }) {
  const [promptOpen, setPromptOpen] = useState(false);
  const prompt = popup.prompt;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4a2c5a]/5 to-[#4a2c5a]/10 rounded-t-2xl">
          <div>
            <h3 className="text-base font-bold text-[#4a2c5a]">{popup.title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Home: {popup.homeLabel} · School: {popup.schoolLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {(popup.items || []).map((ev) => (
            <div key={ev.item_code} className="border rounded-lg p-4 bg-[#4a2c5a]/5 border-[#4a2c5a]/20">
              <div className="text-sm font-medium text-gray-900">
                <span className="text-xs text-gray-400 mr-1.5 font-mono">{ev.item_code}</span>
                {ev.statement_text || ev.description}
              </div>
              <div className="mt-3 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-24 h-6 rounded text-[11px] font-semibold bg-[#4a2c5a]/10 text-[#4a2c5a] border border-[#4a2c5a]/20">
                    Parent / Carer
                  </span>
                  <span className="text-sm text-gray-800">{ev.home_answer || '—'}</span>
                  {ev.home_score != null && (
                    <span className="text-[10px] text-gray-400 tabular-nums">
                      (score: {typeof ev.home_score === 'number' ? ev.home_score.toFixed(1) : ev.home_score})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-24 h-6 rounded text-[11px] font-semibold bg-amber-100 text-gray-800 border border-amber-200">
                    School
                  </span>
                  <span className="text-sm text-gray-800">{ev.school_answer || '—'}</span>
                  {ev.school_score != null && (
                    <span className="text-[10px] text-gray-400 tabular-nums">
                      (score: {typeof ev.school_score === 'number' ? ev.school_score.toFixed(1) : ev.school_score})
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          {(!popup.items || popup.items.length === 0) && (
            <p className="text-sm text-gray-500 py-4 text-left">
              No questionnaire items available for this subdomain.
            </p>
          )}

          {/* Expandable LLM prompt section */}
          {prompt && (
            <div className="mt-4 border rounded-lg border-amber-300 bg-amber-50/60 overflow-hidden">
              <button
                type="button"
                onClick={() => setPromptOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-amber-800 hover:bg-amber-100/50 transition-colors"
              >
                <span>LLM Prompt (system + user input)</span>
                <span className="text-amber-600">{promptOpen ? '▲' : '▼'}</span>
              </button>
              {promptOpen && (
                <div className="px-4 pb-4 space-y-3">
                  <div>
                    <div className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1">System Prompt</div>
                    <pre className="text-xs text-gray-800 bg-white border border-amber-200 rounded-lg p-3 whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto font-mono text-left">{prompt.system_prompt}</pre>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1">User Input (evidence block)</div>
                    <pre className="text-xs text-gray-800 bg-white border border-amber-200 rounded-lg p-3 whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto font-mono text-left">{prompt.user_input}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ANIMATED REVEAL + ADJUSTMENT CARD
// ────────────────────────────────────────────────────────────────────────────

function AnimatedReveal({ show, children, maxH = 'max-h-[500px]' }) {
  return (
    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${show ? `${maxH} opacity-100` : 'max-h-0 opacity-0'}`}>
      {children}
    </div>
  );
}

// Maps backend type labels to emoji + display text for the top-right badge
const RA_TYPE_BADGE = {
  'Environmental': { icon: '🏠', label: 'Environmental' },
  'Scaffolding':   { icon: '🤝', label: 'Scaffolding' },
  'Skill-building': { icon: '🎯', label: 'Skill-building' },
};

function AdjustmentCard({ item, index, devMode }) {
  const [decision, setDecision] = useState(null);
  const [rejectReason, setRejectReason] = useState(null);
  const [retryText, setRetryText] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const [showFramingPrompt, setShowFramingPrompt] = useState(false);

  const shortLabel = item.short_label || '';
  const description = item.text || '';
  const typeLabel = item.type_label || '';
  const typeBadge = RA_TYPE_BADGE[typeLabel] || (typeLabel ? { icon: '📋', label: typeLabel } : null);

  const btnClass = 'px-4 py-2 text-sm font-medium rounded-full bg-[#ced6f6] text-[#4a2c5a] hover:bg-[#b0bce8] transition-all duration-200';

  return (
    <div className="rounded-xl bg-[#eef0fb] border-l-4 border-[#4a2c5a] p-6 text-left flex gap-4 items-start">
      {/* Circled position number */}
      <div className="w-8 h-8 rounded-full bg-[#4a2c5a] text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-bold">
        {index}
      </div>
      <div className="flex-1 space-y-4">
      {/* Heading row: short label on the left, type badge on the right */}
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-lg font-bold text-[#4a2c5a] leading-snug">
          {shortLabel || description}
        </h4>
        {typeBadge && (
          <span className="flex-shrink-0 text-xs font-medium text-[#4a2c5a]/70 bg-white/70 border border-[#4a2c5a]/20 rounded-full px-3 py-1 whitespace-nowrap">
            {typeBadge.icon} {typeBadge.label}
          </span>
        )}
      </div>

      {/* Framing sentence — falls back to the verbose RA description. */}
      {item.framing_sentence ? (
        <p className="text-sm text-gray-700 leading-relaxed">{item.framing_sentence}</p>
      ) : item.framing_error ? (
        <p className="text-sm text-red-400 italic">{description}</p>
      ) : shortLabel ? (
        <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
      ) : null}

      {/* DEV MODE: framing sentence LLM prompt */}
      {devMode && item._framing_llm_prompt && (
        <div>
          <button
            type="button"
            onClick={() => setShowFramingPrompt((v) => !v)}
            className="text-[10px] font-medium text-amber-600 hover:text-amber-800 underline transition-colors"
          >
            {showFramingPrompt ? 'Hide framing prompt' : 'View framing LLM prompt'}
          </button>
          <AnimatedReveal show={devMode && showFramingPrompt} maxH="max-h-96">
            <div className="mt-1 rounded border border-amber-200 bg-amber-50/60 p-3">
              <p className="text-[10px] font-bold uppercase text-amber-700 mb-1">System Prompt</p>
              <pre className="text-[10px] text-amber-900 whitespace-pre-wrap font-mono leading-relaxed max-h-32 overflow-y-auto">{item._framing_system_prompt || ''}</pre>
              <p className="text-[10px] font-bold uppercase text-amber-700 mt-2 mb-1">User Input</p>
              <pre className="text-[10px] text-amber-900 whitespace-pre-wrap font-mono leading-relaxed max-h-32 overflow-y-auto">{item._framing_llm_prompt}</pre>
            </div>
          </AnimatedReveal>
        </div>
      )}

      {/* DEV MODE: Scoring detail — shows why this RA was ranked here */}
      {devMode && (
        <div className="rounded-lg bg-amber-50/70 border border-amber-200 px-4 py-3 space-y-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Ranking Detail</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-amber-800 font-mono">
            {item.icf_total_score != null && (
              <span>ICF total: <strong>{item.icf_total_score}</strong></span>
            )}
            <span>Tag matches: <strong>{item.tag_match_count ?? 0}</strong></span>
            {item.icf_codes?.length > 0 && (
              <span>ICF codes: {item.icf_codes.join(', ')}</span>
            )}
          </div>
          {item.matched_sensory_tags?.length > 0 && (
            <p className="text-xs text-amber-700">Sensory: {item.matched_sensory_tags.join(', ')}</p>
          )}
          {item.matched_sensitivity_tags?.length > 0 && (
            <p className="text-xs text-amber-700">Sensitivity: {item.matched_sensitivity_tags.join(', ')}</p>
          )}
        </div>
      )}

      {/* HOW TO IMPLEMENT — white box with LLM-generated bullets */}
      {item.implementation_bullets?.length > 0 ? (
        <div className="rounded-lg bg-white px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-wider text-[#4a2c5a]">How to implement</p>
            {devMode && item._llm_prompt && (
              <button
                type="button"
                onClick={() => setShowPrompt((v) => !v)}
                className="text-[10px] font-medium text-amber-600 hover:text-amber-800 underline transition-colors"
              >
                {showPrompt ? 'Hide prompt' : 'View LLM prompt'}
              </button>
            )}
          </div>
          {/* DEV MODE: LLM prompt used to generate the bullets */}
          <AnimatedReveal show={devMode && showPrompt} maxH="max-h-96">
            <div className="mb-3 rounded border border-amber-200 bg-amber-50/60 p-3">
              <p className="text-[10px] font-bold uppercase text-amber-700 mb-1">System Prompt</p>
              <pre className="text-[10px] text-amber-900 whitespace-pre-wrap font-mono leading-relaxed max-h-32 overflow-y-auto">{item._llm_system_prompt || ''}</pre>
              <p className="text-[10px] font-bold uppercase text-amber-700 mt-2 mb-1">User Input</p>
              <pre className="text-[10px] text-amber-900 whitespace-pre-wrap font-mono leading-relaxed max-h-32 overflow-y-auto">{item._llm_prompt}</pre>
            </div>
          </AnimatedReveal>
          <ul className="space-y-2">
            {item.implementation_bullets.map((bullet, bi) => (
              <li key={bi} className="flex items-baseline gap-2.5 text-sm text-gray-800 leading-relaxed">
                <span className="text-[#4a2c5a] flex-shrink-0 text-lg leading-none">&bull;</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : item.implementation_error ? (
        <div className="rounded-lg bg-white px-5 py-4">
          <p className="text-xs font-bold uppercase tracking-wider text-[#4a2c5a] mb-2">How to implement</p>
          <p className="text-sm text-gray-500 italic">Could not generate implementation suggestions for this adjustment.</p>
          {devMode && item.implementation_error && (
            <p className="mt-1 text-xs text-red-500 font-mono">{item.implementation_error}</p>
          )}
        </div>
      ) : null}

      {/* NEXT STEPS */}
      <div className="pt-2">
        <p className="text-xs font-bold uppercase tracking-wider text-[#4a2c5a] mb-3">Next steps</p>

        {/* Button row — fades out when a decision is made */}
        <AnimatedReveal show={!decision} maxH="max-h-20">
          <div className="flex flex-wrap gap-2 pb-1">
            <button onClick={() => setDecision('shortlist')} className={btnClass}>Add to shortlist</button>
            <button onClick={() => setDecision('parked')} className={btnClass}>Park for now</button>
            <button onClick={() => setDecision('reject')} className={btnClass}>Reject</button>
            <button onClick={() => setDecision('retry')} className={btnClass}>Retry with changes</button>
          </div>
        </AnimatedReveal>

        {/* Decision confirmation — fades in after selection */}
        <AnimatedReveal show={!!decision} maxH="max-h-96">
          <div className="space-y-3 pb-1">
            <div className="flex items-center gap-3">
              <span className="inline-block px-4 py-2 text-sm font-medium rounded-full bg-[#ddd6fe] text-[#4a2c5a]">
                {decision === 'shortlist' && 'Added to shortlist'}
                {decision === 'parked' && 'Parked for now'}
                {decision === 'reject' && (rejectReason ? `Rejected — ${rejectReason}` : 'Rejected')}
                {decision === 'retry' && 'Retry requested'}
              </span>
              <button
                onClick={() => { setDecision(null); setRejectReason(null); setRetryText(''); }}
                className="text-sm text-[#4a2c5a] underline hover:opacity-70 transition-opacity duration-200"
              >
                Change decision
              </button>
            </div>

            {/* Reject sub-options — slides in when "Reject" is chosen */}
            <AnimatedReveal show={decision === 'reject' && !rejectReason} maxH="max-h-40">
              <div className="pl-1 space-y-1.5 pb-1">
                <p className="text-sm text-gray-600 font-medium">Select a reason:</p>
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => setRejectReason('Not practical')} className="text-left text-sm text-[#4a2c5a] hover:underline transition-colors duration-150">
                    &bull; Not practical
                  </button>
                  <button onClick={() => setRejectReason('Already attempted, not effective')} className="text-left text-sm text-[#4a2c5a] hover:underline transition-colors duration-150">
                    &bull; Already attempted, delivered consistently and was not effective
                  </button>
                  <button onClick={() => setRejectReason('Other')} className="text-left text-sm text-[#4a2c5a] hover:underline transition-colors duration-150">
                    &bull; Other
                  </button>
                </div>
              </div>
            </AnimatedReveal>

            {/* Retry text input — slides in when "Retry" is chosen */}
            <AnimatedReveal show={decision === 'retry'} maxH="max-h-40">
              <div className="pl-1 pb-1">
                <p className="text-sm text-gray-600 font-medium mb-2">Please provide more info:</p>
                <textarea
                  value={retryText}
                  onChange={(e) => setRetryText(e.target.value)}
                  placeholder="Describe what changes you'd like..."
                  className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#4a2c5a]/20 transition-shadow duration-200"
                />
              </div>
            </AnimatedReveal>
          </div>
        </AnimatedReveal>
      </div>
      </div>{/* end flex-1 content wrapper */}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ERROR BOUNDARY — catches rendering crashes inside the report panel so the
// whole page doesn't go blank. Displays a recoverable error message instead.
// ────────────────────────────────────────────────────────────────────────────

class ReportErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[HfpV2Report] Render crash caught by error boundary:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-left space-y-3">
          <h3 className="text-lg font-bold text-red-700">Something went wrong</h3>
          <p className="text-sm text-gray-600">
            The report panel encountered an error while rendering. This is usually
            caused by unexpected data from the backend.
          </p>
          <pre className="text-xs bg-red-50 border border-red-200 rounded-lg p-3 whitespace-pre-wrap max-h-40 overflow-y-auto">
            {this.state.error?.message || 'Unknown error'}
          </pre>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[#4a2c5a] text-white hover:bg-[#3a1c4a] transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// POLISH-STATE HYDRATION
// ────────────────────────────────────────────────────────────────────────────
// The backend returns polished (editor-pass) paragraphs alongside the report.
// We default to showing the polished version wherever one exists, mirroring
// the demo page's behaviour after a report is generated.

function buildShowPolishedDefaults(report) {
  const defaults = {};
  if (!report || typeof report !== 'object') return defaults;

  const pol = report._polished;
  if (pol && typeof pol === 'object') {
    for (const dk of Object.keys(pol)) {
      if (pol[dk]?.polished && Object.keys(pol[dk].polished).length > 0) {
        defaults[dk] = true;
      }
    }
  }
  if (report._strengths_llm?.polished && Object.keys(report._strengths_llm.polished).length > 0) {
    defaults['__strengths__'] = true;
  }
  if (report._challenges_llm?.polished && Object.keys(report._challenges_llm.polished).length > 0) {
    defaults['__challenges__'] = true;
  }
  const gpiPol = report._gpi_polished;
  if (gpiPol && typeof gpiPol === 'object') {
    for (const gk of Object.keys(gpiPol)) {
      if (gpiPol[gk]?.polished && Object.keys(gpiPol[gk].polished).length > 0) {
        defaults['__gpi__'] = true;
        break;
      }
    }
  }
  return defaults;
}

// ────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ────────────────────────────────────────────────────────────────────────────

function ReportBody({ report, childName, devMode, pageMode = false, clinicianView = false }) {
  const isLlmMode = report?._llm_meta?.mode === 'llm';
  const defaultSection = 'welcome';

  const [activeSection, setActiveSection] = useState(defaultSection);
  // All nav groups start collapsed; the clinician expands what they need.
  const [expandedNav, setExpandedNav] = useState({});
  const [adjDetailIndex, setAdjDetailIndex] = useState(null);
  const [evidencePopup, setEvidencePopup] = useState(null);
  const [selectedModelPerSub, setSelectedModelPerSub] = useState({});

  // Polish state derived from the report. Defaults to showing polished output.
  const polishedData = useMemo(
    () => (report?._polished && typeof report._polished === 'object' ? report._polished : {}),
    [report],
  );
  const [showPolished, setShowPolished] = useState(() => buildShowPolishedDefaults(report));

  // When the report (or its mode) changes, reset section + polish defaults.
  useEffect(() => {
    setActiveSection('welcome');
    setExpandedNav({});
    setAdjDetailIndex(null);
    setShowPolished(buildShowPolishedDefaults(report));
  }, [isLlmMode, report]);

  const toggleNav = (sectionId) => {
    setExpandedNav((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // v3 records supply family headings via report._domain_names (keyed by
  // domainKey or nav id); v2 reports render the static labels unchanged.
  const navName = (key, fallback) => (report?._domain_names || {})[key] || fallback;
  const navSections = [
    {
      id: 'aod',
      title: 'AREAS OF DEVELOPMENT',
      items: AOD_DOMAINS.map((d) => ({ id: d.id, label: navName(d.domainKey, d.label) })),
    },
    {
      id: 'activities',
      title: 'ACTIVITIES & ENVIRONMENT',
      items: [
        { id: 'gpi', label: navName('gpi', 'Other Areas of Life') },
        { id: 'environmental', label: navName('environmental', 'Environmental Factors') },
      ],
    },
    {
      id: 'adjustments',
      title: 'REASONABLE ADJUSTMENTS',
      items: [
        { id: 'adjustments-home', label: 'Adjustments — Home' },
        { id: 'adjustments-school', label: 'Adjustments — School' },
      ],
    },
  ];

  // ── Welcome to this page (concise purpose + how to use) ──
  const renderWelcome = () => {
    const subject = childName && childName !== 'the child' ? childName : 'your child';
    const Card = ({ children, className = '' }) => (
      <div className={`rounded-xl border border-gray-200 bg-white p-4 ${className}`}>{children}</div>
    );
    const H = ({ children }) => (
      <h3 className="text-base font-semibold text-[#4a2c5a] mb-1.5">{children}</h3>
    );
    return (
      <div className="space-y-5 text-sm text-gray-700 leading-relaxed">
        <SectionHeaderCard
          title="How to use this report"
          subtitle="A guide for parents, carers and school representatives"
        />

        <Card className="bg-[#eef0fb] border-[#4a2c5a]/20">
          <p>
            This report is <strong>about understanding, not labelling</strong>. Every child
            experiences the world differently. It helps you understand how {subject} functions
            across areas of life — what comes naturally, where they might need extra support, and
            how home and school can work together so they can thrive.
          </p>
        </Card>

        {/* 1. Before you begin */}
        <div>
          <H>1. Before you begin</H>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Card>
              <p className="font-medium text-gray-800 mb-1">What this report is</p>
              <p>A shared picture built from <strong>home and school</strong> observations of how {subject} functions day to day, based on a World Health Organisation framework covering both underlying abilities and everyday activities.</p>
            </Card>
            <Card>
              <p className="font-medium text-gray-800 mb-1">What this report is not</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>A diagnosis or clinical assessment</li>
                <li>A judgement about your child or your parenting</li>
                <li>A fixed label — children change and develop</li>
                <li>Something to &lsquo;fix&rsquo; — differences are not deficits</li>
              </ul>
            </Card>
          </div>
        </div>

        {/* 2. Understanding the colours — uses the exact panels/icons from the report */}
        <div>
          <H>2. Understanding the colours</H>
          <div className="space-y-2 text-left">
            {[
              { bucket: 'STRENGTH_NO_DIFFICULTY', what: 'This area is going well. Celebrate it and consider how these strengths can support other areas.' },
              { bucket: 'SUPPORT_NEED', what: `${subject} may benefit from some support or adjustments here. Small changes can make a real difference.` },
              { bucket: 'PRIORITY_AREA', what: 'An area where focused support would help most. A red area does not mean something is wrong — it is simply where understanding and support matter most.' },
            ].map(({ bucket, what }) => {
              const cfg = STATUS_PANEL_CONFIG[bucket];
              return (
                <div key={bucket} className={`flex items-start gap-3 rounded-xl ${cfg.bg} p-3 text-left`}>
                  <div className="flex-shrink-0">{cfg.icon}</div>
                  <div>
                    <div className="font-semibold text-gray-900">{cfg.text}</div>
                    <p className="text-gray-700">{what}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 3. Navigating the report */}
        <div>
          <H>3. Navigating the report</H>
          <Card>
            <ul className="space-y-2">
              <li><strong>Areas of Development</strong> — how {subject} is doing across Executive Function, Motor &amp; Physical, Speech/Language &amp; Communication, Social &amp; Emotional and Sensory, with Home and School shown side by side and what each pattern may mean.</li>
              <li><strong>Other Areas of Life</strong> — how {subject} takes part in everyday activities and participation.</li>
              <li><strong>Environmental Factors</strong> — the supports and barriers around {subject} at home and at school.</li>
              <li><strong>Reasonable Adjustments</strong> — practical, tailored suggestions: why each matters, what may help, and how to put it in place.</li>
            </ul>
          </Card>
        </div>

        {/* 4. Having the conversation */}
        <div>
          <H>4. Having the conversation</H>
          <Card>
            <p className="mb-2">This report is designed to be <strong>read and discussed together</strong> by home and school. To make the most of it:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Read it in advance and note anything that surprised you.</li>
              <li>Where home and school differ, get curious rather than deciding who is &lsquo;right&rsquo; — both perspectives are valid and together tell the full story (a child may work hard to manage at school and let go at home).</li>
              <li>Move to the adjustments and agree 2&ndash;3 priorities to start with, who will do them, and when you will check in.</li>
            </ul>
          </Card>
        </div>

        {/* 5. Differences are not deficits */}
        <div>
          <H>5. Differences are not deficits</H>
          <Card>
            <p>This report takes a <strong>strengths-based</strong> view and recognises that many difficulties arise from environments that were not designed for a child&rsquo;s way of processing the world — not from the child themselves. The goal is to understand {subject}, build on their strengths, and remove barriers so they can flourish.</p>
          </Card>
        </div>

      </div>
    );
  };

  // ── About This Profile ──
  const renderAbout = () => {
    const data = report?.about_this_profile;
    if (!data) return <SummaryBox text="Section not available." />;
    return (
      <div className="space-y-6">
        <SectionHeaderCard title={data.heading} subtitle="Context and scope of this profile" />
        <div className="rounded-xl border border-gray-200 p-5 bg-white space-y-3">
          {(data.paragraphs || []).map((p, i) => (
            <p key={i} className="text-sm text-gray-700 leading-relaxed">{p}</p>
          ))}
        </div>
      </div>
    );
  };

  // ── At a Glance ──
  const renderAtAGlance = () => {
    const rows = report?.at_a_glance_table || [];
    const narrative = report?.child_at_a_glance_narrative;
    return (
      <div className="space-y-6">
        <SectionHeaderCard
          title={`${childName} at a Glance`}
          subtitle="Overview of functioning across all domains, at home and school"
        />

        {/* Narrative summary (UCR 5-step algorithm output) */}
        {narrative?.narrative && (
          <div className="rounded-xl border-2 border-[#4a2c5a]/20 bg-gradient-to-r from-[#f8f5fa] to-white p-5">
            <p className="text-base text-gray-800 leading-relaxed font-medium">{narrative.narrative}</p>
          </div>
        )}

        <ColourKeyLegend />
        <div className="overflow-hidden rounded-xl bg-white border border-gray-200">
          <table className="w-full text-sm border-separate" style={{ borderSpacing: '0 2px' }}>
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Domain</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Home</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">School</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Agreement</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">CPG</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.domain} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-semibold text-[#4a2c5a]">{row.domain_plain || row.domain}</td>
                  <td className="px-4 py-3 text-left">
                    <ClassificationBadge classification={row.home} mean={row.home_mean} />
                  </td>
                  <td className="px-4 py-3 text-left">
                    <ClassificationBadge classification={row.school} mean={row.school_mean} />
                  </td>
                  <td className="px-4 py-3 text-left text-sm text-gray-700">{row.agreement}</td>
                  <td className="px-4 py-3 text-left">
                    <span className={`text-sm font-medium ${row.divergent ? 'text-red-700' : 'text-gray-700'}`}>
                      {row.cpg}
                      {row.divergent && <span className="ml-1 text-xs text-red-500">⚠</span>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <EvidencePanel hidden={!devMode} items={narrative?.evidence_items} />
        <DebugPanel hidden={!devMode} notes={narrative?.debug_notes} />
      </div>
    );
  };

  // ── Strengths section (deterministic template engine output) ──
  const renderStrengths = () => {
    const data = report?.strengths;
    if (!data) {
      return (
        <div className="space-y-6">
          <SectionHeaderCard
            title={`What's Going Well for ${childName}`}
            subtitle="Areas where he shows strengths and capabilities"
          />
          <p className="text-sm text-gray-500 italic">No strengths were identified in this profile.</p>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <SectionHeaderCard
          title={data.heading || `What's Going Well for ${childName}`}
          subtitle="Areas where he shows strengths and capabilities"
        />
        {data.preamble && (
          <div className="rounded-xl border border-green-200 bg-[#f0f9e8] p-4">
            <p className="text-sm text-gray-700 leading-relaxed">{data.preamble}</p>
          </div>
        )}
        {(data.domains || []).map((d, i) => (
          <div key={i} className="rounded-xl border border-gray-200 p-5 bg-white">
            <h4 className="text-base font-bold text-[#4a2c5a] mb-2 flex items-center gap-2">
              <span className="text-green-600">🟢</span> {d.heading}
            </h4>
            <p className="text-sm text-gray-700 leading-relaxed">{d.text}</p>
            <EvidencePanel hidden={!devMode} items={d.evidence_items} />
          </div>
        ))}
        <DebugPanel hidden={!devMode} notes={data.debug_notes} />
      </div>
    );
  };

  // ── Challenges section (deterministic 7-step algorithm output) ──
  const renderChallenges = () => {
    const data = report?.challenges_summary || {};
    const paragraphs = data.paragraphs || [];
    return (
      <div className="space-y-6">
        <SectionHeaderCard
          title={data.heading || `What's Harder for ${childName}`}
          subtitle="Areas where he needs more support"
        />
        {paragraphs.length > 0 ? (
          paragraphs.map((p, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-5 bg-white">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-yellow-600">🟡</span>
                <h4 className="text-sm font-bold text-[#4a2c5a]">{p.subdomain_plain}</h4>
                <span className="text-xs text-gray-400">({(p.contexts || []).join(', ')})</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{p.text}</p>
            </div>
          ))
        ) : data.mild_fallback ? (
          <div className="rounded-xl border border-gray-200 p-5 bg-white">
            <p className="text-sm text-gray-700 leading-relaxed">{data.mild_fallback}</p>
          </div>
        ) : null}
        {data.additional_mild && (
          <div className="rounded-xl border border-yellow-200 bg-[#fffbeb] p-4">
            <p className="text-sm text-gray-700 leading-relaxed">{data.additional_mild}</p>
          </div>
        )}
        <EvidencePanel hidden={!devMode} items={data.evidence_items} />
        <DebugPanel hidden={!devMode} notes={data.debug_notes} />
      </div>
    );
  };

  // ── In Depth Challenges (per-subdomain structured paragraphs) ──
  const renderInDepth = () => {
    const data = report?.in_depth_challenges;
    if (!data) {
      return (
        <div className="space-y-6">
          <SectionHeaderCard title={`In Depth — Areas Where ${childName} Finds Things Harder`} subtitle="Detailed breakdown by subdomain" />
          <p className="text-sm text-gray-500 italic">No significant challenges to explore in depth.</p>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <SectionHeaderCard title={data.heading} subtitle="Detailed breakdown by subdomain" />
        {(data.domains || []).map((domainSection, di) => (
          <div key={di} className="space-y-4">
            <h3 className="text-lg font-bold text-[#4a2c5a] border-b border-gray-200 pb-2">{domainSection.domain_plain}</h3>
            {(domainSection.paragraphs || []).map((p, pi) => (
              <div key={pi} className="rounded-xl border border-gray-200 p-5 bg-white">
                <h4 className="text-sm font-bold text-[#4a2c5a] mb-2">{p.heading}</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{p.text}</p>
                <EvidencePanel hidden={!devMode} items={p.evidence_items} />
              </div>
            ))}
          </div>
        ))}
        <DebugPanel hidden={!devMode} notes={data.debug_notes} />
      </div>
    );
  };

  // ── The Big Picture (deterministic 5-step assembly) ──
  const renderBiggerPicture = () => {
    const data = report?.the_big_picture || {};
    return (
      <div className="space-y-6">
        <SectionHeaderCard
          title={data.heading || 'The Big Picture'}
          subtitle={`Understanding the overall pattern of ${childName}'s functioning`}
        />
        {data.narrative && (
          <div className="rounded-xl border border-gray-200 p-5 bg-white">
            <p className="text-sm text-gray-700 leading-relaxed">{data.narrative}</p>
          </div>
        )}
        <EvidencePanel hidden={!devMode} items={data.evidence_items} />
        <DebugPanel hidden={!devMode} notes={data.debug_notes} />
      </div>
    );
  };

  // ── How These Findings Connect (bigger picture connections) ──
  const renderConnections = () => {
    const data = report?.bigger_picture_connections;
    if (!data) return null;
    return (
      <div className="space-y-6">
        <SectionHeaderCard
          title={data.heading || 'How These Findings Connect'}
          subtitle="Understanding how different areas of functioning relate to each other"
        />
        <div className="rounded-xl border border-gray-200 p-5 bg-white space-y-3">
          {(data.paragraphs || []).map((p, i) => (
            <p key={i} className="text-sm text-gray-700 leading-relaxed">{p}</p>
          ))}
        </div>
        <EvidencePanel hidden={!devMode} items={data.evidence_items} />
        <DebugPanel hidden={!devMode} notes={data.debug_notes} />
      </div>
    );
  };

  // ── GPI / Other Areas of Life ──
  const renderGPI = () => {
    const gpiAreas = report?.gpi_areas || {};
    const gpiDomainKey = Object.keys(gpiAreas).find((k) => !k.startsWith('_'));
    const domainData = gpiDomainKey ? gpiAreas[gpiDomainKey] : null;
    const gpiPolished = report?._gpi_polished || {};
    const domainPolish = gpiDomainKey ? gpiPolished[gpiDomainKey] : null;
    const llmMeta = report?._llm_meta || {};
    const modelLabels = llmMeta.model_labels || {};
    const modelReady = llmMeta.model_ready || {};
    const isMultiModel = (llmMeta.model_ids || []).length > 1;

    const GPI_KEY = '__gpi__';
    const hasPolished = domainPolish?.polished && Object.keys(domainPolish.polished).length > 0;
    const isShowingPolished = showPolished[GPI_KEY] && hasPolished;

    if (!domainData || !domainData.rows || domainData.rows.length === 0) {
      return (
        <div>
          <div className="text-left pb-6 border-b border-gray-200 mb-8">
            <h1 className="text-xl md:text-2xl font-extrabold text-[#4a2c5a]">Other Areas of Life</h1>
            <p className="mt-2 text-base text-gray-600">How {childName} manages and participates in daily life activities.</p>
          </div>
          <p className="text-sm text-gray-500 italic text-left">No data available for this section.</p>
        </div>
      );
    }

    const rows = domainData.rows || [];
    const hasAnyLlm = rows.some((r) =>
      r.llm_paragraph || (r.llm_outputs && Object.keys(r.llm_outputs).length > 0)
    );

    return (
      <div>
        {/* Page header — matches domain page style */}
        <div className="text-left pb-6 border-b border-gray-200 mb-8">
          <h1 className="text-xl md:text-2xl font-extrabold text-[#4a2c5a]">Other Areas of Life</h1>
          <p className="mt-2 text-base text-gray-600">How {childName} manages and participates in daily life activities.</p>
        </div>

        {/* Original / Polished toggle + editor prompt viewer (dev mode only) */}
        {devMode && hasAnyLlm && hasPolished && (
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
              <button
                type="button"
                onClick={() => setShowPolished((p) => ({ ...p, [GPI_KEY]: false }))}
                className={`px-3 py-1.5 transition-colors ${
                  !isShowingPolished ? 'bg-[#4a2c5a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >Original</button>
              <button
                type="button"
                onClick={() => setShowPolished((p) => ({ ...p, [GPI_KEY]: true }))}
                className={`px-3 py-1.5 transition-colors ${
                  isShowingPolished ? 'bg-[#4a2c5a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >Polished</button>
            </div>
            {domainPolish?.editor_prompt && (
              <button
                type="button"
                onClick={() => setEvidencePopup({
                  title: 'Editor Prompt — Other Areas of Life',
                  homeLabel: '—', schoolLabel: '—', items: [],
                  prompt: domainPolish.editor_prompt,
                })}
                title="View editor prompt"
                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-600 transition-colors shadow-sm"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Per-subdomain blocks — mirrors domain page layout */}
        <div className="space-y-6">
          {rows.map((row) => {
            const homeLabel = row.home_label || '—';
            const schoolLabel = row.school_label || '—';
            const evidence = row.evidence_items || [];
            const outputs = row.llm_outputs || {};
            const modelIds = Object.keys(outputs);
            const subKey = row.subdomain;
            const subPlain = row.subdomain_plain || row.subdomain;

            const activeMid = selectedModelPerSub[`gpi_${subKey}`] || modelIds[0] || null;
            const activeOutput = activeMid ? outputs[activeMid] : null;

            const originalParagraph = modelIds.length > 1
              ? (activeOutput?.paragraph || null)
              : (row.llm_paragraph || activeOutput?.paragraph || null);
            const displayError = modelIds.length > 1
              ? (activeOutput?.error || null)
              : (row.llm_error || activeOutput?.error || null);

            const polishedParagraph = domainPolish?.polished?.[subPlain] || null;
            const displayParagraph = (isShowingPolished && polishedParagraph)
              ? polishedParagraph
              : originalParagraph;

            return (
              <div key={subKey}>
                {/* Subdomain heading with evidence button */}
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-xl font-semibold text-[#4a2c5a]">{subPlain}</h3>
                  {devMode && evidence.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setEvidencePopup({
                        title: subPlain,
                        homeLabel,
                        schoolLabel,
                        items: evidence,
                        prompt: row.llm_prompt || null,
                      })}
                      title="View questionnaire statements, answers, and LLM prompt"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-400 hover:text-[#4a2c5a] transition-colors shadow-sm"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </button>
                  )}
                  {devMode && isShowingPolished && polishedParagraph && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                      POLISHED
                    </span>
                  )}
                </div>

                {/* AT HOME / AT SCHOOL status panels */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4 justify-start">
                  <StatusPanel context="home" classification={homeLabel} mean={row.home_mean} isSensory={false} devMode={devMode} labelText={row.home_label_text} />
                  {!row.home_only && (
                    <StatusPanel context="school" classification={schoolLabel} mean={row.school_mean} isSensory={false} devMode={devMode} labelText={row.school_label_text} />
                  )}
                </div>

                {/* Multi-model radio selector (dev mode only) */}
                {devMode && isMultiModel && !isShowingPolished && modelIds.length > 1 && (
                  <div className="flex items-center gap-0 mb-3 rounded-lg border border-gray-300 overflow-hidden w-fit" role="radiogroup">
                    {modelIds.map((mid) => {
                      const isReady = modelReady[mid] !== false;
                      const isSelected = (selectedModelPerSub[`gpi_${subKey}`] || modelIds[0]) === mid;
                      return (
                        <label key={mid} className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors border-r last:border-r-0 border-gray-300 ${
                          isSelected ? 'bg-[#4a2c5a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                        } ${!isReady ? 'opacity-50' : ''}`}>
                          <input
                            type="radio"
                            className="sr-only"
                            checked={isSelected}
                            onChange={() => setSelectedModelPerSub((p) => ({ ...p, [`gpi_${subKey}`]: mid }))}
                          />
                          {!isReady && <span title="API key not configured">🔑</span>}
                          {modelLabels[mid] || mid}
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* LLM paragraph (or placeholder) */}
                {displayParagraph ? (
                  <p className="text-base text-[#4a2c5a] leading-relaxed text-left">{displayParagraph}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic leading-relaxed text-left">
                    {displayError
                      ? `Narrative unavailable: ${displayError}`
                      : 'No comparison paragraph available for this area.'}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Evidence trail and debug notes */}
        <div className="mt-4">
          <EvidencePanel hidden={!devMode} items={domainData?.evidence_items} />
        </div>
      </div>
    );
  };

  // ── Suggested Adjustments (two-level drill-down) ──
  const ADJ_BUCKET_STYLES = {
    'Priority Area': {
      rowBg: 'bg-rose-100',
      numBg: 'bg-rose-600 text-white',
    },
    'Support Need': {
      rowBg: 'bg-amber-100',
      numBg: 'bg-amber-500 text-white',
    },
  };
  const ADJ_DEFAULT_STYLE = ADJ_BUCKET_STYLES['Support Need'];

  const renderAdjustmentsPage = (contextKey, contextLabel) => {
    const adj = report?.suggested_adjustments;
    if (!adj) {
      // HFP v3 (TD-210): adjustments are pending the clinical re-specification
      // of the matching rules against the new instrument — say so honestly
      // rather than rendering a blank page.
      if (report?._v3) {
        return (
          <div className="space-y-8">
            <SectionHeaderCard
              title={`Adjustments — ${contextLabel}`}
              subtitle={`What could be done at ${contextLabel.toLowerCase()} to support ${childName}`}
            />
            <SummaryBox text="Suggested adjustments for this profile are being prepared and will appear here once ready." />
          </div>
        );
      }
      return null;
    }

    const ctxData = adj[contextKey] || {};
    const groups = ctxData.groups || [];
    const name = childName || 'the child';

    // ── Detail page (drilled into a single subdomain) ──
    if (adjDetailIndex != null && adjDetailIndex < groups.length) {
      const group = groups[adjDetailIndex];

      const nextGroup = adjDetailIndex < groups.length - 1 ? groups[adjDetailIndex + 1] : null;

      return (
        <div>
          {/* Page header — matches domain page style */}
          <div className="text-left pb-6 border-b border-gray-200 mb-8">
            <h1 className="text-xl md:text-2xl font-extrabold text-[#4a2c5a]">Adjustments for {group.subdomain}</h1>
            <p className="mt-2 text-base text-gray-600">What could be done at {contextLabel.toLowerCase()} to help {name} with {group.subdomain.toLowerCase()}</p>
          </div>

          {/* "Why this matters" — narrative explaining why this subdomain was
              flagged, colour-coded by bucket. */}
          {(() => {
            const isPriority = group.bucket === 'Priority Area';
            const whyBg   = isPriority ? 'bg-[#ffe5e5]' : 'bg-[#fef3cc]';
            const whyBdr  = isPriority ? 'border-red-500' : 'border-yellow-400';
            const whyIcon = isPriority
              ? (
                <svg viewBox="0 0 24 24" className="w-8 h-8 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              )
              : (
                <svg viewBox="0 0 24 24" className="w-8 h-8 text-yellow-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 11L12 6L14 8" /><path d="M17 13L12 18L10 16" />
                  <path d="M7 11C4.5 13 4.5 17 7 19C9.5 21 13.5 21 16 19" />
                  <path d="M17 13C19.5 11 19.5 7 17 5C14.5 3 10.5 3 8 5" />
                </svg>
              );
            return (
          <div className="mt-14 mb-8">
            <h3 className="text-xl font-semibold text-[#4a2c5a] mb-4">Why this matters</h3>
            {group.why_matters_text ? (
              <div className={`rounded-xl ${whyBg} border-l-4 ${whyBdr} px-6 py-5 flex gap-4 items-start text-left`}>
                {whyIcon}
                <p className="text-sm text-gray-700 leading-relaxed text-left">
                  {group.why_matters_text}
                </p>
              </div>
            ) : group.why_matters_error ? (
              <div className={`rounded-xl ${whyBg} border-l-4 ${whyBdr} px-6 py-5 text-left`}>
                <p className="text-sm text-gray-500 italic text-left">Could not generate narrative for this area.</p>
                {devMode && (
                  <p className="mt-1 text-xs text-red-500 font-mono text-left">{group.why_matters_error}</p>
                )}
              </div>
            ) : (
              <div className={`rounded-xl ${whyBg} border-l-4 ${whyBdr} px-6 py-5 text-left`}>
                <p className="text-sm text-gray-400 italic text-left">No narrative available for this area.</p>
              </div>
            )}

            {/* Dev mode: show the LLM prompt used for "why this matters" */}
            {devMode && group._why_matters_llm_prompt && (
              <div className="mt-2 rounded border border-amber-200 bg-amber-50/60 p-3">
                <p className="text-[10px] font-bold uppercase text-amber-700 mb-1">System Prompt</p>
                <pre className="text-[10px] text-amber-900 whitespace-pre-wrap font-mono leading-relaxed max-h-32 overflow-y-auto">{group._why_matters_system_prompt || ''}</pre>
                <p className="text-[10px] font-bold uppercase text-amber-700 mt-2 mb-1">User Input</p>
                <pre className="text-[10px] text-amber-900 whitespace-pre-wrap font-mono leading-relaxed max-h-40 overflow-y-auto">{group._why_matters_llm_prompt}</pre>
              </div>
            )}
          </div>
            );
          })()}

          {/* "What may help" — individual RA cards */}
          <div className="mt-14 space-y-5 mb-8">
            <h3 className="text-xl font-semibold text-[#4a2c5a]">What may help {name}</h3>
            {(group.adjustments || []).map((item, ai) => (
              <AdjustmentCard key={ai} item={item} index={ai + 1} devMode={devMode} />
            ))}
          </div>

          {/* Dev mode: Option B (tag-match ranking) comparison */}
          {devMode && group.option_b_adjustments?.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-5 mb-8 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
                Option B Ranking (tag-match priority) — {group.option_b_adjustments.length} adjustments
              </p>
              <p className="text-xs text-amber-700">
                These are the same candidate adjustments ranked by tag-match count first (ICF score as tiebreaker) instead of ICF score first (Option A, shown above).
              </p>
              <div className="space-y-2">
                {group.option_b_adjustments.map((bItem, bi) => (
                  <div key={bi} className="flex items-start gap-3 text-xs text-amber-900 bg-white/70 rounded-lg px-3 py-2 border border-amber-100">
                    <span className="font-bold text-amber-600 flex-shrink-0 mt-0.5">{bi + 1}.</span>
                    <div className="flex-1">
                      <p className="font-medium">{bItem.text}</p>
                      <p className="text-amber-700 font-mono mt-0.5">
                        Tags: {bItem.tag_match_count ?? 0} | ICF: {bItem.icf_total_score ?? '—'} | {bItem.type_label || bItem.category || '—'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Potential Impact — summary of combined benefit */}
          {group.impact_bullets?.length > 0 && (
            <div className="mt-14 mb-8">
              <h3 className="text-xl font-semibold text-[#4a2c5a] text-left mb-4">Potential impact</h3>
              <div className="rounded-xl bg-[#f0f7f0] border-l-4 border-green-500 px-6 py-5 flex gap-4 items-start">
                <svg className="w-8 h-8 text-green-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="currentColor">
                  <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                </svg>
                <div className="text-left">
                  <p className="font-semibold text-gray-800 mb-2">
                    Implementing any of these adjustments may help {name}:
                  </p>
                  <ul className="space-y-1.5">
                    {group.impact_bullets.map((bullet, bi) => (
                      <li key={bi} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed">
                        <span className="text-green-600 mt-0.5 flex-shrink-0">&bull;</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Dev mode: show the LLM prompt used for impact bullets */}
              {devMode && group._impact_llm_prompt && (
                <div className="mt-2 rounded border border-amber-200 bg-amber-50/60 p-3">
                  <p className="text-[10px] font-bold uppercase text-amber-700 mb-1">System Prompt</p>
                  <pre className="text-[10px] text-amber-900 whitespace-pre-wrap font-mono leading-relaxed max-h-32 overflow-y-auto">{group._impact_system_prompt || ''}</pre>
                  <p className="text-[10px] font-bold uppercase text-amber-700 mt-2 mb-1">User Input</p>
                  <pre className="text-[10px] text-amber-900 whitespace-pre-wrap font-mono leading-relaxed max-h-40 overflow-y-auto">{group._impact_llm_prompt}</pre>
                </div>
              )}
            </div>
          )}

          {/* Show error in dev mode if impact generation failed */}
          {devMode && group.impact_error && !group.impact_bullets?.length && (
            <div className="mb-8 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <strong>Impact bullets error:</strong> {group.impact_error}
            </div>
          )}

          {/* Dev mode: RA engine decision trace */}
          <DebugPanel hidden={!devMode} notes={adj.debug_notes} />

          {/* Back / Forward navigation */}
          <div className="flex justify-between items-center gap-4 pt-2">
            <button
              onClick={() => setAdjDetailIndex(null)}
              className="px-4 py-2 rounded-lg bg-[#4a2c5a] text-white text-sm font-medium hover:bg-[#3a1c4a] transition-colors"
            >
              &larr; Back to: {contextLabel} Adjustments
            </button>
            {nextGroup && (
              <button
                onClick={() => setAdjDetailIndex(adjDetailIndex + 1)}
                className="px-4 py-2 rounded-lg bg-[#4a2c5a] text-white text-sm font-medium hover:bg-[#3a1c4a] transition-colors"
              >
                Go to: Adjustments for {nextGroup.subdomain} &rarr;
              </button>
            )}
          </div>
        </div>
      );
    }

    // ── Landing page (list of impaired subdomains) ──
    return (
      <div className="space-y-0">
        {/* Page header — matches domain page style */}
        <div className="text-left pb-6 border-b border-gray-200 mb-8">
          <h1 className="text-xl md:text-2xl font-extrabold text-[#4a2c5a]">Suggested Adjustments at {contextLabel}</h1>
          <p className="mt-2 text-base text-gray-600">What could be done at {contextLabel.toLowerCase()} to set {name} up for success at {contextLabel.toLowerCase()}</p>
        </div>

        {!groups.length ? (
          <div className="text-gray-400 text-sm py-8">
            No adjustments identified for {contextLabel}. This typically means
            no subdomain reached the impairment threshold (mean at or below −1)
            in this setting.
          </div>
        ) : (
          <div className="space-y-5 text-left">
            <h3 className="text-xl font-semibold text-[#4a2c5a]">Top priority areas</h3>
            <p className="text-sm text-gray-600 leading-relaxed text-left">
              Based on {name}&apos;s profile, we&apos;ve identified the areas where
              adjustments could make a significant difference.
            </p>

            {/* Clickable subdomain rows — colour-coded by bucket, centred */}
            <div className="flex flex-col items-center gap-2 py-2">
              {groups.map((group, gi) => {
                const style = ADJ_BUCKET_STYLES[group.bucket] || ADJ_DEFAULT_STYLE;
                return (
                  <button
                    key={gi}
                    onClick={() => setAdjDetailIndex(gi)}
                    className={`w-full max-w-lg flex items-center gap-3 ${style.rowBg} rounded-xl px-4 py-3 hover:opacity-80 transition-opacity text-left`}
                  >
                    <span className={`flex-shrink-0 w-7 h-7 rounded-full ${style.numBg} text-sm font-bold flex items-center justify-center`}>
                      {gi + 1}
                    </span>
                    <span className="font-semibold text-sm text-gray-800 flex-1">{group.subdomain}</span>
                    <span className="text-gray-400 text-lg flex-shrink-0">&rsaquo;</span>
                  </button>
                );
              })}
            </div>

            <p className="text-sm text-gray-500 text-left">
              Click on any to see specific strategies, why they matter and how they might help.
            </p>
          </div>
        )}

        {/* Dev mode: RA engine decision trace */}
        <DebugPanel hidden={!devMode} notes={adj.debug_notes} />
      </div>
    );
  };

  // ── Next Steps ──
  const renderNextSteps = () => {
    const data = report?.next_steps;
    if (!data) return null;
    return (
      <div className="space-y-6">
        <SectionHeaderCard title={data.heading || 'Next Steps'} subtitle="Suggested actions based on this profile" />
        <div className="space-y-3">
          {(data.suggestions || []).map((s, i) => (
            <div key={i} className="flex gap-3 rounded-xl border border-gray-200 p-4 bg-white">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#4a2c5a] text-white text-sm font-bold flex items-center justify-center">{i + 1}</span>
              <p className="text-sm text-gray-700 leading-relaxed pt-0.5">{s}</p>
            </div>
          ))}
        </div>
        <DebugPanel hidden={!devMode} notes={data.debug_notes} />
      </div>
    );
  };

  // ── Domain detail page (Areas of Development) ──
  // v3 family headings (guarded): the adapter supplies report._domain_names
  const domainDisplayName = (entry) =>
    (report?._domain_names || {})[entry.domainKey] ||
    (report?._domain_names || {})[entry.id] ||
    entry.label;

  const renderDomainPage = (aodEntry) => {
    const areas = report?.areas_of_development || {};
    const domainData = areas[aodEntry.domainKey];
    const llmMeta = report?._llm_meta || {};
    const modelLabels = llmMeta.model_labels || {};
    const modelReady = llmMeta.model_ready || {};

    if (!domainData) {
      return (
        <div className="space-y-6">
          <SectionHeaderCard title={domainDisplayName(aodEntry)} subtitle={aodEntry.subtitle(childName)} />
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-left text-sm text-gray-500">
            No data available for this domain.
          </div>
        </div>
      );
    }

    const rows = domainData?.rows || [];
    const hasAnyLlm = rows.some((r) =>
      r.llm_paragraph || (r.llm_outputs && Object.keys(r.llm_outputs).length > 0)
    );

    const dk = aodEntry.domainKey;
    const domainPolish = polishedData[dk];
    const isShowingPolished = showPolished[dk] && domainPolish?.polished;

    return (
      <div className="space-y-0">
        {/* Domain header */}
        <div className="text-left pb-6 border-b border-gray-200 mb-8">
          <h1 className="text-xl md:text-2xl font-extrabold text-[#4a2c5a]">{aodEntry.label}</h1>
          <p className="mt-2 text-base text-gray-600">{aodEntry.subtitle(childName)}</p>
        </div>

        {/* Original / Polished toggle + editor prompt viewer (dev mode only) */}
        {devMode && hasAnyLlm && domainPolish?.polished && Object.keys(domainPolish.polished).length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
              <button
                type="button"
                onClick={() => setShowPolished((p) => ({ ...p, [dk]: false }))}
                className={`px-3 py-1.5 transition-colors ${
                  !isShowingPolished
                    ? 'bg-[#4a2c5a] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Original
              </button>
              <button
                type="button"
                onClick={() => setShowPolished((p) => ({ ...p, [dk]: true }))}
                className={`px-3 py-1.5 transition-colors ${
                  isShowingPolished
                    ? 'bg-[#4a2c5a] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Polished
              </button>
            </div>

            {devMode && domainPolish?.editor_prompt && (
              <button
                type="button"
                onClick={() => setEvidencePopup({
                  title: `Editor Prompt — ${aodEntry.label}`,
                  homeLabel: '—',
                  schoolLabel: '—',
                  items: [],
                  prompt: domainPolish.editor_prompt,
                })}
                title="View editor prompt"
                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-600 transition-colors shadow-sm"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Per-subdomain blocks */}
        <div className="space-y-6">
          {rows.map((row) => {
            const homeLabel = row.home_label || '—';
            const schoolLabel = row.school_label || '—';
            const evidence = row.evidence_items || [];
            const outputs = row.llm_outputs || {};
            const modelIds = Object.keys(outputs);
            const isMultiModel = modelIds.length > 1;
            const subKey = row.subdomain;
            const subPlain = row.subdomain_plain || row.subdomain;

            const activeMid = selectedModelPerSub[subKey] || modelIds[0] || null;
            const activeOutput = activeMid ? outputs[activeMid] : null;

            const originalParagraph = isMultiModel
              ? (activeOutput?.paragraph || null)
              : (row.llm_paragraph || activeOutput?.paragraph || null);
            const displayError = isMultiModel
              ? (activeOutput?.error || null)
              : (row.llm_error || activeOutput?.error || null);

            const polishedParagraph = domainPolish?.polished?.[subPlain] || null;

            const displayParagraph = (isShowingPolished && polishedParagraph)
              ? polishedParagraph
              : originalParagraph;

            return (
              <div key={subKey}>
                {/* Subdomain heading with magnifying glass evidence button */}
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-xl font-semibold text-[#4a2c5a]">
                    {subPlain}
                  </h3>
                  {devMode && evidence.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setEvidencePopup({
                        title: subPlain,
                        homeLabel,
                        schoolLabel,
                        items: evidence,
                        prompt: row.llm_prompt || null,
                      })}
                      title="View questionnaire statements, answers, and LLM prompt"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-400 hover:text-[#4a2c5a] transition-colors shadow-sm"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </button>
                  )}

                  {devMode && isShowingPolished && polishedParagraph && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                      POLISHED
                    </span>
                  )}
                </div>

                {/* AT HOME / AT SCHOOL status panels */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4 justify-start">
                  <StatusPanel context="home" classification={homeLabel} mean={row.home_mean} isSensory={row.is_sensory} devMode={devMode} labelText={row.home_label_text} />
                  <StatusPanel context="school" classification={schoolLabel} mean={row.school_mean} isSensory={row.is_sensory} devMode={devMode} labelText={row.school_label_text} />
                </div>

                {/* Horizontal radio selector for switching between LLM model
                    outputs per subdomain (dev mode only). */}
                {devMode && isMultiModel && !isShowingPolished && (
                  <div className="flex items-center gap-0 mb-3 rounded-lg border border-gray-300 overflow-hidden w-fit" role="radiogroup" aria-label={`Model selector for ${subPlain}`}>
                    {modelIds.map((mid, idx) => {
                      const label = modelLabels[mid] || mid;
                      const hasError = outputs[mid]?.error;
                      const noKey = modelReady[mid] === false;
                      const isActive = mid === activeMid;
                      return (
                        <button
                          key={mid}
                          type="button"
                          role="radio"
                          aria-checked={isActive}
                          title={noKey ? `API key not configured for ${mid}` : hasError ? `${label} — generation failed` : label}
                          onClick={() => setSelectedModelPerSub((prev) => ({ ...prev, [subKey]: mid }))}
                          className={[
                            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-all',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4a2c5a]/40 focus-visible:z-10',
                            idx > 0 ? 'border-l border-gray-300' : '',
                            isActive
                              ? 'bg-[#4a2c5a] text-white shadow-inner'
                              : noKey
                                ? 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                                : hasError
                                  ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                  : 'bg-white text-gray-600 hover:bg-gray-50',
                          ].join(' ')}
                        >
                          {/* Radio dot indicator */}
                          <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 border ${
                            isActive
                              ? 'bg-white border-white'
                              : 'bg-transparent border-gray-400'
                          }`} />
                          {label}
                          {noKey && <span className="text-gray-400" title="No API key">🔑</span>}
                          {hasError && !noKey && <span className={isActive ? 'text-red-300' : 'text-red-400'}>✗</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* LLM paragraph (or placeholder) */}
                {displayParagraph ? (
                  <p className="text-base text-[#4a2c5a] leading-relaxed text-left">{displayParagraph}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic leading-relaxed text-left">
                    {displayError
                      ? `Narrative unavailable: ${displayError}`
                      : 'No narrative available for this area.'}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* HFP v3 pain safety note (INSTRUCTION-family-report-narrative,
            2026-07-22): NOT model-generated — rendered verbatim from the pain
            override flag, because it is the one sentence with a safety
            consequence if paraphrased, softened or dropped. The style content
            is absorbed into the per-sense narratives. */}
        {aodEntry.domainKey === 'Sensory' && report?._pain_safety && (
          <div
            className="mt-6 rounded-xl border p-4 text-sm"
            style={{ background: '#fdf3f3', borderColor: '#f5dede', color: '#7f1d1d' }}
          >
            <span className="font-bold mr-1.5">Safety note:</span>
            {childName} may not notice pain the way other children do; worth everyone
            knowing, so injuries and illness are not missed.
          </div>
        )}

        {/* Evidence trail and debug notes */}
        <div className="mt-4">
          <EvidencePanel hidden={!devMode} items={domainData?.evidence_items} />
          <DebugPanel hidden={!devMode} notes={areas._debug_notes} />
        </div>
      </div>
    );
  };

  // ── Sensory Profile (deterministic, 13 systems) ──
  const renderSensoryProfile = () => {
    const data = report?.sensory_profile || {};
    const systems = data.systems || [];
    return (
      <div className="space-y-6">
        <SectionHeaderCard
          title={data.heading || 'Sensory Profile'}
          subtitle={`How ${childName} experiences and processes sensory information`}
        />
        {data.preamble && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
            <p className="text-sm text-gray-700 leading-relaxed">{data.preamble}</p>
          </div>
        )}
        {systems.map((sys, i) => (
          <div key={i} className="rounded-xl border border-gray-200 p-5 bg-white">
            <h4 className="text-sm font-bold text-[#4a2c5a] mb-2">{sys.system}</h4>
            {(sys.paragraphs || []).map((p, pi) => (
              <p key={pi} className="text-sm text-gray-700 leading-relaxed mb-2">{p}</p>
            ))}
          </div>
        ))}
        {data.closing && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm text-gray-600 italic leading-relaxed">{data.closing}</p>
          </div>
        )}
        <EvidencePanel hidden={!devMode} items={data.evidence_items} />
        <DebugPanel hidden={!devMode} notes={data.debug_notes} />
      </div>
    );
  };

  // ── Environmental Factors (uses the same StatusPanel + key as domain pages) ──
  const renderEnvironmental = () => {
    const envSummary = report?.environmental_summary || {};
    const subdomains = Array.isArray(envSummary?.subdomains) ? envSummary.subdomains : [];

    // Map an environmental subdomain's mean e-code score to a difficulty
    // classification so it renders with the same Strength / Support need /
    // Priority panels and colour key as the other domain pages. Environmental
    // supports (positive) read as no difficulty; barriers (negative) as a
    // support need or priority area.
    const classifyEnv = (side) => {
      const items = side?.items || [];
      if (!items.length) return { classification: 'Not enough information', mean: null };
      const mean = items.reduce((a, i) => a + (i.score || 0), 0) / items.length;
      let classification;
      if (mean >= 0) classification = 'No difficulty';
      else if (mean >= -1) classification = 'Mild difficulty';
      else classification = 'Moderate or severe difficulty';
      return { classification, mean };
    };

    const summaryFor = (sub) => {
      const parts = [];
      ['home', 'school'].forEach((ctx) => {
        const items = (sub[ctx] || {}).items || [];
        const barriers = items.filter((i) => i.score < 0).map((i) => i.description);
        const supports = items.filter((i) => i.score > 0).map((i) => i.description);
        const label = ctx === 'home' ? 'At home' : 'At school';
        if (!barriers.length && !supports.length) {
          parts.push(`${label}, no significant barriers or supports were reported.`);
          return;
        }
        const bits = [];
        if (barriers.length) bits.push(`barriers include ${barriers.slice(0, 3).join(', ')}`);
        if (supports.length) bits.push(`supports include ${supports.slice(0, 3).join(', ')}`);
        parts.push(`${label}, ${bits.join('; ')}.`);
      });
      return parts.join(' ');
    };

    return (
      <div className="space-y-8">
        <SectionHeaderCard
          title="Environmental Factors"
          subtitle={`How ${childName}'s environment supports or constrains everyday functioning`}
        />
        {subdomains.length === 0 ? (
          <SummaryBox text="No environmental factors available." />
        ) : (
          <>
            <div className="space-y-6">
              {subdomains.map((sub) => {
                // HFP v3 (TD-210): the adapter supplies resolved environmental
                // labels (barrier/facilitator words); v2 keeps the mean-derived
                // classification unchanged.
                const home = sub.home_label
                  ? { classification: sub.home_label, mean: null }
                  : classifyEnv(sub.home);
                const school = sub.school_label
                  ? { classification: sub.school_label, mean: null }
                  : classifyEnv(sub.school);
                return (
                  <div key={sub.name}>
                    <h3 className="text-xl font-semibold text-[#4a2c5a] mb-4">{sub.name}</h3>
                    <div className="flex flex-col sm:flex-row gap-3 mb-4 justify-start">
                      <StatusPanel context="home" classification={home.classification} mean={home.mean} isSensory={false} devMode={devMode} labelText={sub.home_label} />
                      <StatusPanel context="school" classification={school.classification} mean={school.mean} isSensory={false} devMode={devMode} labelText={sub.school_label} />
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{sub.narrative || summaryFor(sub)}</p>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  // ── Strengths (LLM-enhanced) ──
  const renderStrengthsLlm = () => {
    const strengthsData = report?.strengths;
    const strengthsPolish = report?._strengths_llm;
    const llmMeta = report?._llm_meta || {};
    const modelLabels = llmMeta.model_labels || {};
    const modelReady = llmMeta.model_ready || {};
    const isMultiModel = (llmMeta.model_ids || []).length > 1;

    const STRENGTHS_KEY = '__strengths__';
    const hasPolished = strengthsPolish?.polished && Object.keys(strengthsPolish.polished).length > 0;
    const isShowingPolished = showPolished[STRENGTHS_KEY] && hasPolished;

    if (!strengthsData || !strengthsData.domains || strengthsData.domains.length === 0) {
      return (
        <div className="space-y-6">
          <div className="text-left pb-6 border-b border-gray-200 mb-8">
            <h1 className="text-xl md:text-2xl font-extrabold text-[#4a2c5a]">Strengths</h1>
            <p className="mt-2 text-base text-gray-600">Areas where {childName} shows strengths and capabilities.</p>
          </div>
          <p className="text-sm text-gray-500 italic text-left">No strengths were identified in this profile.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-left pb-6 border-b border-gray-200 mb-8">
          <h1 className="text-xl md:text-2xl font-extrabold text-[#4a2c5a]">Strengths</h1>
          <p className="mt-2 text-base text-gray-600">Areas where {childName} shows strengths and capabilities.</p>
        </div>

        {/* Preamble */}
        {strengthsData.preamble && (
          <div className="rounded-xl border border-green-200 bg-[#f0f9e8] p-4">
            <p className="text-sm text-gray-700 leading-relaxed text-left">{strengthsData.preamble}</p>
          </div>
        )}

        {/* Original / Polished toggle + editor prompt (dev mode only) */}
        {devMode && hasPolished && (
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
              <button
                type="button"
                onClick={() => setShowPolished((p) => ({ ...p, [STRENGTHS_KEY]: false }))}
                className={`px-3 py-1.5 transition-colors ${
                  !isShowingPolished ? 'bg-[#4a2c5a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >Original</button>
              <button
                type="button"
                onClick={() => setShowPolished((p) => ({ ...p, [STRENGTHS_KEY]: true }))}
                className={`px-3 py-1.5 transition-colors ${
                  isShowingPolished ? 'bg-[#4a2c5a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >Polished</button>
            </div>
            {devMode && strengthsPolish?.editor_prompt && (
              <button
                type="button"
                onClick={() => setEvidencePopup({
                  title: 'Editor Prompt — Strengths',
                  homeLabel: '—', schoolLabel: '—', items: [],
                  prompt: strengthsPolish.editor_prompt,
                })}
                title="View editor prompt"
                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-600 transition-colors shadow-sm"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Per-domain strength paragraphs */}
        {strengthsData.domains.map((domainEntry, di) => {
          const heading = domainEntry.heading;
          const evidence = domainEntry.evidence_items || [];
          const llmPrompt = domainEntry.llm_prompt;
          const llmOutputs = domainEntry.llm_outputs || {};
          const hasLlm = domainEntry.llm_paragraph || Object.keys(llmOutputs).length > 0;

          let displayParagraph = null;
          if (isShowingPolished && strengthsPolish?.polished?.[heading]) {
            displayParagraph = strengthsPolish.polished[heading];
          } else if (hasLlm) {
            const selectedMid = selectedModelPerSub[`str_${heading}`];
            if (selectedMid && llmOutputs[selectedMid]?.paragraph) {
              displayParagraph = llmOutputs[selectedMid].paragraph;
            } else {
              displayParagraph = domainEntry.llm_paragraph;
              if (!displayParagraph) {
                for (const mid of Object.keys(llmOutputs)) {
                  if (llmOutputs[mid]?.paragraph) {
                    displayParagraph = llmOutputs[mid].paragraph;
                    break;
                  }
                }
              }
            }
          }

          return (
            <div key={di} className="space-y-3">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold text-[#4a2c5a]">
                  <span className="text-green-600 mr-1">●</span> {heading}
                </h3>
                {/* Evidence magnifying glass */}
                {devMode && evidence.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setEvidencePopup({
                      title: `Evidence — ${heading}`,
                      homeLabel: 'Parent/Carer', schoolLabel: 'Teacher',
                      items: evidence,
                      prompt: llmPrompt || null,
                    })}
                    title="View evidence and prompt"
                    className="inline-flex items-center justify-center w-6 h-6 rounded-lg border border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-500 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Multi-model radio buttons (dev mode only) */}
              {devMode && isMultiModel && hasLlm && !isShowingPolished && (
                <div className="flex flex-wrap gap-2">
                  {Object.keys(llmOutputs).map((mid) => {
                    const isReady = modelReady[mid] !== false;
                    const isSelected = (selectedModelPerSub[`str_${heading}`] || Object.keys(llmOutputs).find(m => llmOutputs[m]?.paragraph)) === mid;
                    return (
                      <label key={mid} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer border transition-colors ${
                        isSelected ? 'bg-[#4a2c5a] text-white border-[#4a2c5a]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      } ${!isReady ? 'opacity-50' : ''}`}>
                        <input
                          type="radio"
                          name={`str-model-${heading}`}
                          className="sr-only"
                          checked={isSelected}
                          onChange={() => setSelectedModelPerSub((p) => ({ ...p, [`str_${heading}`]: mid }))}
                        />
                        {!isReady && <span title="API key not configured">🔑</span>}
                        {modelLabels[mid] || mid}
                      </label>
                    );
                  })}
                </div>
              )}

              {displayParagraph ? (
                <p className="text-sm text-[#4a2c5a] leading-relaxed text-left">{displayParagraph}</p>
              ) : (
                <p className="text-sm text-gray-400 italic text-left">No narrative available for this domain.</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Challenges (LLM-enhanced) ──
  const renderChallengesLlm = () => {
    const challengesData = report?.challenges_summary;
    const challengesPolish = report?._challenges_llm;
    const llmMeta = report?._llm_meta || {};
    const modelLabels = llmMeta.model_labels || {};
    const modelReady = llmMeta.model_ready || {};
    const isMultiModel = (llmMeta.model_ids || []).length > 1;

    const CHALLENGES_KEY = '__challenges__';
    const hasPolished = challengesPolish?.polished && Object.keys(challengesPolish.polished).length > 0;
    const isShowingPolished = showPolished[CHALLENGES_KEY] && hasPolished;

    const paragraphs = challengesData?.paragraphs || [];

    return (
      <div className="space-y-6">
        <div className="text-left pb-6 border-b border-gray-200 mb-8">
          <h1 className="text-xl md:text-2xl font-extrabold text-[#4a2c5a]">Challenges</h1>
          <p className="mt-2 text-base text-gray-600">Areas where {childName} needs more support.</p>
        </div>

        {/* Original / Polished toggle + editor prompt (dev mode only) */}
        {devMode && hasPolished && (
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
              <button
                type="button"
                onClick={() => setShowPolished((p) => ({ ...p, [CHALLENGES_KEY]: false }))}
                className={`px-3 py-1.5 transition-colors ${
                  !isShowingPolished ? 'bg-[#4a2c5a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >Original</button>
              <button
                type="button"
                onClick={() => setShowPolished((p) => ({ ...p, [CHALLENGES_KEY]: true }))}
                className={`px-3 py-1.5 transition-colors ${
                  isShowingPolished ? 'bg-[#4a2c5a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >Polished</button>
            </div>
            {devMode && challengesPolish?.editor_prompt && (
              <button
                type="button"
                onClick={() => setEvidencePopup({
                  title: 'Editor Prompt — Challenges',
                  homeLabel: '—', schoolLabel: '—', items: [],
                  prompt: challengesPolish.editor_prompt,
                })}
                title="View editor prompt"
                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-600 transition-colors shadow-sm"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            )}
          </div>
        )}

        {paragraphs.length > 0 ? (
          paragraphs.map((para, pi) => {
            const subPlain = para.subdomain_plain || '';
            const contexts = para.contexts || [];
            const llmPrompt = para.llm_prompt;
            const llmOutputs = para.llm_outputs || {};
            const hasLlm = para.llm_paragraph || Object.keys(llmOutputs).length > 0;

            let displayParagraph = null;
            if (isShowingPolished && challengesPolish?.polished?.[subPlain]) {
              displayParagraph = challengesPolish.polished[subPlain];
            } else if (hasLlm) {
              const selectedMid = selectedModelPerSub[`chal_${subPlain}`];
              if (selectedMid && llmOutputs[selectedMid]?.paragraph) {
                displayParagraph = llmOutputs[selectedMid].paragraph;
              } else {
                displayParagraph = para.llm_paragraph;
                if (!displayParagraph) {
                  for (const mid of Object.keys(llmOutputs)) {
                    if (llmOutputs[mid]?.paragraph) {
                      displayParagraph = llmOutputs[mid].paragraph;
                      break;
                    }
                  }
                }
              }
            }

            return (
              <div key={pi} className="space-y-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-semibold text-[#4a2c5a]">{subPlain}</h3>
                  <span className="text-xs text-gray-400">({contexts.join(', ')})</span>
                  {/* Evidence magnifying glass */}
                  {devMode && llmPrompt && (
                    <button
                      type="button"
                      onClick={() => setEvidencePopup({
                        title: `Evidence — ${subPlain}`,
                        homeLabel: 'Parent/Carer', schoolLabel: 'Teacher',
                        items: [],
                        prompt: llmPrompt,
                      })}
                      title="View evidence and prompt"
                      className="inline-flex items-center justify-center w-6 h-6 rounded-lg border border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-500 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Multi-model radio buttons (dev mode only) */}
                {devMode && isMultiModel && hasLlm && !isShowingPolished && (
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(llmOutputs).map((mid) => {
                      const isReady = modelReady[mid] !== false;
                      const isSelected = (selectedModelPerSub[`chal_${subPlain}`] || Object.keys(llmOutputs).find(m => llmOutputs[m]?.paragraph)) === mid;
                      return (
                        <label key={mid} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer border transition-colors ${
                          isSelected ? 'bg-[#4a2c5a] text-white border-[#4a2c5a]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        } ${!isReady ? 'opacity-50' : ''}`}>
                          <input
                            type="radio"
                            name={`chal-model-${subPlain}`}
                            className="sr-only"
                            checked={isSelected}
                            onChange={() => setSelectedModelPerSub((p) => ({ ...p, [`chal_${subPlain}`]: mid }))}
                          />
                          {!isReady && <span title="API key not configured">🔑</span>}
                          {modelLabels[mid] || mid}
                        </label>
                      );
                    })}
                  </div>
                )}

                {displayParagraph ? (
                  <p className="text-sm text-[#4a2c5a] leading-relaxed text-left">{displayParagraph}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic text-left">No narrative available for this subdomain.</p>
                )}
              </div>
            );
          })
        ) : challengesData?.mild_fallback ? (
          <p className="text-sm text-gray-700 leading-relaxed text-left">{challengesData.mild_fallback}</p>
        ) : (
          <p className="text-sm text-gray-500 italic text-left">No significant challenges were reported.</p>
        )}

        {/* Mild difficulties addendum (deterministic, always shown if present) */}
        {challengesData?.additional_mild && (
          <div className="rounded-xl border border-yellow-200 bg-[#fffbeb] p-4">
            <p className="text-sm text-gray-700 leading-relaxed text-left">{challengesData.additional_mild}</p>
          </div>
        )}
      </div>
    );
  };

  // ── The Big Picture (LLM synthesis) ──
  const renderBigPictureLlm = () => {
    const bigPicture = report?._big_picture_llm;
    const paragraphs = bigPicture?.paragraphs || [];
    const hasError = bigPicture?.error;
    const masterPrompt = bigPicture?.master_prompt;

    return (
      <div className="space-y-6">
        <div className="text-left pb-6 border-b border-gray-200 mb-8">
          <h1 className="text-xl md:text-2xl font-extrabold text-[#4a2c5a]">The Big Picture</h1>
          <p className="mt-2 text-base text-gray-600">
            How {childName}'s findings connect across domains — cross-cutting themes, underlying patterns, and what they mean together.
          </p>
        </div>

        {/* Magnifying glass to view the master editor prompt */}
        {devMode && masterPrompt && (
          <div className="flex items-center gap-2 mb-4">
            <button
              type="button"
              onClick={() => setEvidencePopup({
                title: 'Master Editor Prompt — The Big Picture',
                homeLabel: '—',
                schoolLabel: '—',
                items: [],
                prompt: masterPrompt,
              })}
              title="View master editor prompt and input data"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-medium transition-colors shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              View prompt &amp; input data
            </button>
          </div>
        )}

        {devMode && hasError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <strong>Error:</strong> {bigPicture.error}
          </div>
        )}

        {paragraphs.length > 0 ? (
          <div className="space-y-5">
            {paragraphs.map((para, i) => (
              <p key={i} className="text-sm text-[#4a2c5a] leading-relaxed text-left">
                {para}
              </p>
            ))}
          </div>
        ) : !hasError ? (
          <div className="text-left text-sm text-gray-500">
            No &quot;Big Picture&quot; content available for this profile.
          </div>
        ) : null}
      </div>
    );
  };

  // ── Section routing ──
  const renderContent = () => {
    // Check if this is an Areas of Development domain page (aod-*)
    const aodEntry = AOD_DOMAINS.find((d) => d.id === activeSection);
    if (aodEntry) return renderDomainPage(aodEntry);

    switch (activeSection) {
      case 'welcome': return renderWelcome();
      case 'about': return renderAbout();
      case 'at-a-glance': return renderAtAGlance();
      case 'strengths': return renderStrengths();
      case 'challenges': return renderChallenges();
      case 'in-depth': return renderInDepth();
      case 'bigger-picture': return renderBiggerPicture();
      case 'connections': return renderConnections();
      case 'sensory-profile': return renderSensoryProfile();
      case 'gpi': return renderGPI();
      case 'next-steps': return renderNextSteps();
      case 'adjustments-home': return renderAdjustmentsPage('home', 'Home');
      case 'adjustments-school': return renderAdjustmentsPage('school', 'School');
      case 'environmental': return renderEnvironmental();
      case 'strengths-llm': return renderStrengthsLlm();
      case 'challenges-llm': return renderChallengesLlm();
      case 'activities-llm': return renderGPI();
      case 'big-picture-llm': return renderBigPictureLlm();
      default: return null;
    }
  };

  // Flat, ordered list of selectable sections for the prev/next nav footer.
  // The standalone "How to use this report" item leads.
  const flatNavItems = useMemo(
    () => [
      { id: 'welcome', label: 'How to use this report' },
      ...navSections.flatMap((s) => s.items).filter((i) => !i.disabled),
    ],
    [navSections],
  );
  const currentNavIndex = flatNavItems.findIndex((i) => i.id === activeSection);
  const goToNavItem = (idx) => {
    const item = flatNavItems[idx];
    if (!item) return;
    setActiveSection(item.id);
    setAdjDetailIndex(null);
  };

  return (
    <div className={pageMode ? 'flex h-full min-h-0' : 'flex min-h-[80vh]'}>
      {/* ── Sidebar navigation (collapsible groups + colour key) ── */}
      <div className="w-[220px] flex-shrink-0 hidden md:flex flex-col border-r border-gray-200 bg-gradient-to-b from-[#4a2c5a]/5 to-[#4a2c5a]/10">
        {/* Title */}
        <div className="px-4 pt-5 pb-3">
          <h3 className="text-sm font-extrabold text-[#4a2c5a] leading-tight text-center">
            Holistic<br />Functional Profile
          </h3>
        </div>

        {/* Scrollable nav area */}
        <div className="flex-1 overflow-y-auto px-3 pb-3">
          <nav className="space-y-1">
            {/* Standalone top-level item (no dropdown) */}
            <button
              type="button"
              onClick={() => { setActiveSection('welcome'); setAdjDetailIndex(null); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                activeSection === 'welcome'
                  ? 'bg-white shadow-sm border border-gray-200 text-[#4a2c5a] font-semibold'
                  : 'text-gray-600 hover:bg-white/60 hover:text-gray-800'
              }`}
            >
              How to use this report
            </button>

            {navSections.map((section) => {
              const isExpanded = expandedNav[section.id];
              return (
                <div key={section.id}>
                  {/* Section group header — toggles collapse */}
                  <button
                    type="button"
                    onClick={() => toggleNav(section.id)}
                    className="w-full flex items-center justify-between px-2 py-2.5 text-left text-xs font-bold text-[#4a2c5a] uppercase tracking-wider hover:bg-white/50 rounded-lg transition-colors"
                  >
                    <span>{section.title}</span>
                    <svg
                      className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Collapsible child items */}
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="pl-1 pr-0.5 py-0.5 space-y-0.5">
                      {section.items.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          disabled={item.disabled}
                          onClick={() => {
                            if (item.disabled) return;
                            setActiveSection(item.id);
                            setAdjDetailIndex(null);
                            if (!isExpanded) toggleNav(section.id);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                            item.disabled
                              ? 'text-gray-400 cursor-not-allowed italic'
                              : activeSection === item.id
                                ? 'bg-white shadow-sm border border-gray-200 text-[#4a2c5a] font-semibold'
                                : 'text-gray-600 hover:bg-white/60 hover:text-gray-800'
                          }`}
                        >
                          {item.label}{item.disabled ? ' (coming soon)' : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>
        </div>

        {/* Fixed colour key at bottom of sidebar */}
        <SidebarColourKey />
      </div>

      {/* ── Mobile nav (horizontal scroll, flat list) ── */}
      <div className="md:hidden w-full mb-4 overflow-x-auto flex gap-2 pb-2 px-2">
        {flatNavItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => { setActiveSection(item.id); setAdjDetailIndex(null); }}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              activeSection === item.id
                ? 'bg-[#4a2c5a] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* ── Content pane: only this region scrolls (sidebar + key stay put) ── */}
      <div className="flex-1 min-w-0 flex flex-col bg-white">
        <div className="flex-1 overflow-y-auto">
          <div className="hfp-report-body p-4 lg:p-6 mx-auto text-sm text-left" style={{ maxWidth: '900px' }}>
            {clinicianView && (
              <div className="mb-5 flex items-center gap-2 rounded-lg border border-[#4a2c5a]/20 bg-[#4a2c5a]/5 px-4 py-2.5">
                <svg className="w-4 h-4 flex-shrink-0 text-[#4a2c5a]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                <span className="text-xs font-semibold uppercase tracking-wide text-[#4a2c5a]">Service User View</span>
                <span className="text-xs text-gray-600">— this is the report as the service user sees it.</span>
              </div>
            )}
            {renderContent()}
            {/* HFP v3 (TD-210): the calibration line renders in the report footer */}
            {report?._calibration_note && (
              <p className="mt-8 pt-3 border-t border-gray-200 text-[11px] text-gray-500">
                {report._calibration_note}
              </p>
            )}
          </div>
        </div>

        {/* Navigation footer: page through the report sections */}
        {flatNavItems.length > 0 && currentNavIndex !== -1 && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-3 flex items-center justify-between gap-3">
            <button
              type="button"
              disabled={currentNavIndex <= 0}
              onClick={() => goToNavItem(currentNavIndex - 1)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              &larr; Previous
            </button>
            <span className="text-xs text-gray-500 truncate max-w-[40%] text-center">
              {flatNavItems[currentNavIndex]?.label}
            </span>
            <button
              type="button"
              disabled={currentNavIndex >= flatNavItems.length - 1}
              onClick={() => goToNavItem(currentNavIndex + 1)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next &rarr;
            </button>
          </div>
        )}
      </div>

      {/* ── Evidence popup overlay (magnifying glass) — dev mode only ── */}
      {evidencePopup && (
        <EvidencePopup
          popup={evidencePopup}
          onClose={() => setEvidencePopup(null)}
        />
      )}
    </div>
  );
}

/**
 * HfpV2Report — public entry point. Wraps the report body in an error boundary
 * so a malformed section cannot blank the whole page.
 */
export default function HfpV2Report({ report, childName, devMode = false, pageMode = false, clinicianView = false }) {
  const name = childName || report?.child_name || 'the child';
  return (
    <ReportErrorBoundary>
      <ReportBody report={report} childName={name} devMode={devMode} pageMode={pageMode} clinicianView={clinicianView} />
    </ReportErrorBoundary>
  );
}
